'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TimelinePhase {
  id: number;
  phase: string;
  title: string;
  duration: string;
  description: string;
  deliverables: string[];
  icon: string;
}

const ProcessTimeline = () => {
  const [activePhase, setActivePhase] = useState<number>(1);

  const timelinePhases: TimelinePhase[] = [
    {
      id: 1,
      phase: 'Phase 1',
      title: 'Discovery & Strategy',
      duration: '1-2 Weeks',
      description: 'We begin with a comprehensive analysis of your current operations, identifying automation opportunities and defining clear success metrics. This phase establishes the foundation for measurable AI transformation.',
      deliverables: [
        'Current state assessment report',
        'AI opportunity mapping',
        'ROI projection analysis',
        'Strategic implementation roadmap',
        'Success metrics framework'
      ],
      icon: 'MagnifyingGlassIcon'
    },
    {
      id: 2,
      phase: 'Phase 2',
      title: 'System Design & Architecture',
      duration: '2-3 Weeks',
      description: 'Our team designs custom AI solutions tailored to your business processes. We create detailed technical specifications and integration plans that ensure seamless deployment.',
      deliverables: [
        'Technical architecture blueprint',
        'Integration specifications',
        'Data flow diagrams',
        'Security and compliance framework',
        'User experience mockups'
      ],
      icon: 'CubeIcon'
    },
    {
      id: 3,
      phase: 'Phase 3',
      title: 'Development & Integration',
      duration: '3-6 Weeks',
      description: 'We build and integrate AI systems into your existing infrastructure. Our agile approach ensures continuous feedback and refinement throughout the development process.',
      deliverables: [
        'Custom AI model development',
        'System integration completion',
        'Quality assurance testing',
        'Performance optimization',
        'Documentation and training materials'
      ],
      icon: 'CodeBracketIcon'
    },
    {
      id: 4,
      phase: 'Phase 4',
      title: 'Testing & Optimization',
      duration: '1-2 Weeks',
      description: 'Rigorous testing ensures your AI systems perform flawlessly. We validate accuracy, optimize performance, and prepare your team for successful deployment.',
      deliverables: [
        'Comprehensive system testing',
        'Performance benchmarking',
        'User acceptance testing',
        'Final optimization adjustments',
        'Deployment readiness certification'
      ],
      icon: 'BeakerIcon'
    },
    {
      id: 5,
      phase: 'Phase 5',
      title: 'Launch & Training',
      duration: '1 Week',
      description: 'We deploy your AI systems and provide comprehensive training to ensure your team can leverage the full potential of their new capabilities.',
      deliverables: [
        'Production deployment',
        'Team training sessions',
        'User documentation',
        'Support resources',
        'Launch success metrics'
      ],
      icon: 'RocketLaunchIcon'
    },
    {
      id: 6,
      phase: 'Phase 6',
      title: 'Ongoing Support & Optimization',
      duration: 'Continuous',
      description: 'Our partnership continues with ongoing monitoring, optimization, and support. We ensure your AI systems evolve with your business needs and deliver sustained value.',
      deliverables: [
        'Performance monitoring',
        'Continuous optimization',
        'Regular strategy reviews',
        'Priority technical support',
        'Quarterly business reviews'
      ],
      icon: 'ChartBarIcon'
    }
  ];

  return (
    <div className="w-full">
      {/* Desktop Timeline */}
      <div className="hidden lg:block">
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute top-16 left-0 right-0 h-1 bg-border">
            <div 
              className="h-full bg-accent transition-all duration-500"
              style={{ width: `${(activePhase / timelinePhases.length) * 100}%` }}
            />
          </div>

          {/* Timeline Phases */}
          <div className="grid grid-cols-6 gap-4 mb-12">
            {timelinePhases.map((phase) => (
              <button
                key={phase.id}
                onClick={() => setActivePhase(phase.id)}
                className={`relative flex flex-col items-center transition-all duration-300 ${
                  activePhase === phase.id ? 'scale-110' : 'opacity-60 hover:opacity-100'
                }`}
              >
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 transition-all duration-300 ${
                    activePhase === phase.id
                      ? 'bg-accent text-accent-foreground shadow-elevated'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  <Icon name={phase.icon as any} size={28} />
                </div>
                <span className="text-xs font-heading font-semibold text-center">
                  {phase.phase}
                </span>
              </button>
            ))}
          </div>

          {/* Active Phase Details */}
          {timelinePhases.map((phase) => (
            <div
              key={phase.id}
              className={`transition-all duration-500 ${
                activePhase === phase.id
                  ? 'opacity-100 translate-y-0' :'opacity-0 translate-y-4 absolute pointer-events-none'
              }`}
            >
              <div className="bg-card rounded-xl p-8 shadow-soft border border-border">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-heading font-bold text-foreground mb-2">
                      {phase.title}
                    </h3>
                    <p className="text-sm font-sans font-semibold text-accent">
                      Duration: {phase.duration}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setActivePhase(Math.max(1, activePhase - 1))}
                      disabled={activePhase === 1}
                      className="p-2 rounded-lg hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Icon name="ChevronLeftIcon" size={20} />
                    </button>
                    <button
                      onClick={() => setActivePhase(Math.min(timelinePhases.length, activePhase + 1))}
                      disabled={activePhase === timelinePhases.length}
                      className="p-2 rounded-lg hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Icon name="ChevronRightIcon" size={20} />
                    </button>
                  </div>
                </div>

                <p className="text-base text-foreground/80 mb-6 leading-relaxed">
                  {phase.description}
                </p>

                <div>
                  <h4 className="text-sm font-heading font-semibold text-foreground mb-3">
                    Key Deliverables:
                  </h4>
                  <ul className="space-y-2">
                    {phase.deliverables.map((deliverable, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <Icon
                          name="CheckCircleIcon"
                          size={20}
                          className="text-success flex-shrink-0 mt-0.5"
                        />
                        <span className="text-sm text-foreground/80">{deliverable}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile Timeline */}
      <div className="lg:hidden space-y-6">
        {timelinePhases.map((phase, index) => (
          <div key={phase.id} className="relative">
            {index !== timelinePhases.length - 1 && (
              <div className="absolute left-8 top-20 bottom-0 w-0.5 bg-border" />
            )}
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-16 h-16 rounded-full bg-accent text-accent-foreground flex items-center justify-center shadow-soft">
                <Icon name={phase.icon as any} size={28} />
              </div>
              <div className="flex-1 bg-card rounded-xl p-6 shadow-soft border border-border">
                <div className="mb-4">
                  <span className="text-xs font-heading font-semibold text-accent">
                    {phase.phase}
                  </span>
                  <h3 className="text-xl font-heading font-bold text-foreground mt-1">
                    {phase.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Duration: {phase.duration}
                  </p>
                </div>
                <p className="text-sm text-foreground/80 mb-4 leading-relaxed">
                  {phase.description}
                </p>
                <div>
                  <h4 className="text-xs font-heading font-semibold text-foreground mb-2">
                    Key Deliverables:
                  </h4>
                  <ul className="space-y-2">
                    {phase.deliverables.map((deliverable, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <Icon
                          name="CheckCircleIcon"
                          size={16}
                          className="text-success flex-shrink-0 mt-0.5"
                        />
                        <span className="text-xs text-foreground/80">{deliverable}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProcessTimeline;